import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { config } from "../lib/config";
import { GoogleG } from "./SignInButton";

// Same copy as the legacy /login page so credential messaging stays consistent
// (FR-015, Constitution III).
const CREDENTIAL_ERRORS: Record<string, string> = {
  invalid_credentials: "That email or password is incorrect.",
  email_unverified:
    "Your email isn't verified yet. Check your inbox for the verification code.",
  account_disabled: "This account has been disabled. Contact an administrator.",
};

// Polite, no-request-access message for an authenticated-but-non-allowlisted
// identity (FR-018).
const NOT_ALLOWLISTED = "Access isn't available for that account yet.";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type FieldErrors = { email?: string; password?: string };

type ProviderId = "google";
type ProviderDescriptor = { id: ProviderId; label: string };

// Data-driven provider list (FR-011). Apple is intentionally omitted; adding a
// future provider is one entry here plus a render branch below.
const PROVIDERS: ProviderDescriptor[] = [
  { id: "google", label: "Continue with Google" },
];

export function SignInDialog({ onClose }: { onClose: () => void }) {
  const {
    renderGoogleButton,
    loginWithPassword,
    googlePending,
    googleError,
    clearGoogleError,
  } = useAuth();

  const cardRef = useRef<HTMLDivElement>(null);
  const googleHostRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const googleEnabled = Boolean(config.googleClientId);
  const pending = submitting || googlePending;

  // Clear any stale Google error from a previous attempt when the dialog opens.
  useEffect(() => {
    clearGoogleError();
  }, [clearGoogleError]);

  // Mount the real GIS button into the host (no-op when Google isn't configured).
  useEffect(() => {
    if (!googleEnabled) return;
    const host = googleHostRef.current;
    if (host) renderGoogleButton(host);
  }, [googleEnabled, renderGoogleButton]);

  // Capture the trigger, move focus into the dialog, restore focus on close.
  useLayoutEffect(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const card = cardRef.current;
    const first = card?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? card)?.focus();
    return () => {
      restoreFocusRef.current?.focus();
    };
  }, []);

  function focusables(): HTMLElement[] {
    const card = cardRef.current;
    if (!card) return [];
    return Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE));
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const items = focusables();
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function validate(): boolean {
    const next: FieldErrors = {};
    const trimmed = email.trim();
    if (!trimmed) next.email = "Enter your email.";
    else if (!EMAIL_RE.test(trimmed)) next.email = "Enter a valid email.";
    if (!password) next.password = "Enter your password.";
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (pending) return; // prevent duplicate concurrent submits (FR-014)
    setFormError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await loginWithPassword(email.trim().toLowerCase(), password);
      // On success `user` updates → App's gate swaps in the app shell and this
      // dialog unmounts; no explicit navigation needed.
    } catch (err) {
      if (err instanceof ApiError && CREDENTIAL_ERRORS[err.code]) {
        setFormError(CREDENTIAL_ERRORS[err.code]);
      } else {
        setFormError("Sign in failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const visibleError = formError ?? (googleError ? NOT_ALLOWLISTED : null);

  return (
    <div
      className="signin-overlay"
      data-testid="signin-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="signin-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="signin-title"
        ref={cardRef}
        onKeyDown={onKeyDown}
      >
        <button
          type="button"
          className="signin-close"
          aria-label="Close sign-in"
          onClick={onClose}
        >
          ×
        </button>

        <div className="signin-header">
          <span className="signin-mark" aria-hidden>
            PB
          </span>
          <h2 id="signin-title" className="signin-title">
            Sign in to Predabet
          </h2>
          <p className="signin-subtitle">
            Invite-only access · a No Ego Studio project
          </p>
        </div>

        {visibleError ? (
          <p className="signin-form-error" role="alert">
            {visibleError}
          </p>
        ) : null}

        {googleEnabled ? (
          <>
            <div className="signin-providers">
              {PROVIDERS.map((p) =>
                p.id === "google" ? (
                  <div key={p.id} className="signin-provider">
                    <div className="signin-provider-pill" aria-hidden>
                      <GoogleG />
                      <span>{googlePending ? "Signing in…" : p.label}</span>
                    </div>
                    <div
                      ref={googleHostRef}
                      className="signin-google-host"
                      aria-label="Sign in with Google"
                    />
                  </div>
                ) : null,
              )}
            </div>
            <div className="signin-divider">
              <span>or use your email</span>
            </div>
          </>
        ) : null}

        <form className="signin-form" onSubmit={onSubmit} noValidate>
          <label className="signin-label" htmlFor="signin-email">
            Email
            <input
              id="signin-email"
              type="email"
              autoComplete="email"
              value={email}
              disabled={pending}
              aria-invalid={fieldErrors.email ? true : undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {fieldErrors.email ? (
            <span className="signin-field-error" role="alert">
              {fieldErrors.email}
            </span>
          ) : null}

          <label className="signin-label" htmlFor="signin-password">
            Password
            <input
              id="signin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              disabled={pending}
              aria-invalid={fieldErrors.password ? true : undefined}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {fieldErrors.password ? (
            <span className="signin-field-error" role="alert">
              {fieldErrors.password}
            </span>
          ) : null}

          <div className="signin-forgot">
            <Link to="/reset-password" onClick={onClose}>
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="btn-primary signin-submit"
            disabled={pending}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="signin-footer-line">
          Invite-only · Predabet does not advertise.
        </p>
        <p className="signin-legal-line">
          A personal analytics project for entertainment use. Not betting
          advice. 21+.
        </p>
      </div>
    </div>
  );
}
